import type { ClientMsg, ServerMsg } from "../src/shared/protocol.ts";
import { TableRoom } from "../src/server/room.ts";

/** How the UI talks to a table. Same interface whether the table is remote or running in this tab. */
export interface Transport {
  send(msg: ClientMsg): void;
  onMessage(cb: (msg: ServerMsg) => void): void;
  onStatus(cb: (connected: boolean) => void): void;
  /** Called whenever a (re)connection is open; the app sends `join` here. */
  onOpen(cb: () => void): void;
  close(): void;
}

/**
 * Real multiplayer: WebSocket to the table's Durable Object, with automatic reconnect.
 * Pass several URLs to try in turn until one connects (inside Discord the socket may need the /.proxy prefix).
 */
export class WebSocketTransport implements Transport {
  private ws: WebSocket | null = null;
  private msgCb: (m: ServerMsg) => void = () => {};
  private statusCb: (c: boolean) => void = () => {};
  private openCb: () => void = () => {};
  private closed = false;
  private retry = 0;
  private urlIndex = 0;
  private everOpened = false;

  constructor(private urls: string[]) {
    this.connect();
  }

  private connect(): void {
    const ws = new WebSocket(this.urls[this.urlIndex]!);
    this.ws = ws;
    ws.onopen = () => {
      this.everOpened = true;
      this.retry = 0;
      this.statusCb(true);
      this.openCb();
    };
    ws.onmessage = (e) => this.msgCb(JSON.parse(String(e.data)) as ServerMsg);
    ws.onclose = () => {
      this.statusCb(false);
      if (this.closed) return;
      // Never managed to connect? Try the next candidate URL.
      if (!this.everOpened) this.urlIndex = (this.urlIndex + 1) % this.urls.length;
      const wait = Math.min(8000, 500 * 2 ** this.retry++);
      setTimeout(() => this.connect(), wait);
    };
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }
  onMessage(cb: (m: ServerMsg) => void) { this.msgCb = cb; }
  onStatus(cb: (c: boolean) => void) { this.statusCb = cb; }
  onOpen(cb: () => void) { this.openCb = cb; }
  close() { this.closed = true; this.ws?.close(); }
}

/** Offline demo: the whole table (with bots) runs inside this browser tab. No server needed. */
export class LocalTransport implements Transport {
  readonly room: TableRoom;
  private msgCb: (m: ServerMsg) => void = () => {};
  private statusCb: (c: boolean) => void = () => {};
  private openCb: () => void = () => {};
  private id = "local-" + Math.random().toString(36).slice(2, 8);

  constructor(opts: { bots?: number } = {}) {
    this.room = new TableRoom({ allowBots: true, turnMs: 25_000, nextHandMs: 7_000, firstHandMs: 1_800 });
    this.room.connect({ id: this.id, send: (m) => queueMicrotask(() => this.msgCb(structuredClone(m))) });
    this.pendingBots = opts.bots ?? 0;
    setTimeout(() => { this.statusCb(true); this.openCb(); }, 0);
  }

  pendingBots: number;

  send(msg: ClientMsg): void {
    setTimeout(() => {
      this.room.receive(this.id, msg);
      // After the player sits, bring in the requested number of bots
      if (msg.t === "sit" && this.pendingBots > 0) {
        for (let i = 0; i < this.pendingBots; i++) this.room.receive(this.id, { t: "addBot" });
        this.pendingBots = 0;
      }
    }, 0);
  }
  onMessage(cb: (m: ServerMsg) => void) { this.msgCb = cb; }
  onStatus(cb: (c: boolean) => void) { this.statusCb = cb; }
  onOpen(cb: () => void) { this.openCb = cb; }
  close() { this.room.disconnect(this.id); }
}
